import React, { useMemo } from "react";
import TitleStripe from "../common/TitleStripe";
import AvisoCard from "../common/query/AvisoCard";
import { formatToReal, hasFalenciaRecuperacaoCode } from "../../utils/utils";
import { FiAlertTriangle } from "react-icons/fi";

const DESCR = {
  pendencias: {
    short: "Dívidas e atrasos registrados por empresas e instituições.",
    long:
      "As pendências financeiras representam dívidas em aberto ou atrasadas informadas por bancos, comércios e prestadores de serviço. " +
      "Elas indicam que o consultado teve dificuldades em manter pagamentos em dia e impactam diretamente o score e a confiança no crédito.",
  },
  restricoes: {
    short:
      "Registros negativos que indicam inadimplência ou débitos não pagos.",
    long:
      "Restrições são registros de inadimplência informados por bureaus de crédito, como SPC e Serasa. " +
      "Elas mostram que o consultado possui dívidas vencidas e não quitadas, servindo de alerta para o risco de crédito.",
  },
  pendenciasBancarias: {
    short: "Atrasos ou renegociações com bancos e financeiras.",
    long:
      "As pendências bancárias abrangem operações em atraso com instituições financeiras, como empréstimos e cartões. " +
      "Sinalizam que houve inadimplência ou renegociação e podem restringir novas liberações de crédito e financiamentos.",
  },
  acoesCiveisBureau: {
    short:
      "Processos judiciais relacionados a cobranças e disputas financeiras.",
    long:
      "Ações cíveis indicam que o consultado está envolvido em processos de cobrança, execução ou litígios contratuais. " +
      "Essas informações ajudam a avaliar o risco jurídico e financeiro, considerando o tipo, valor e fase das ações.",
  },
  chequesSemFundoBACEN: {
    short: "Cheques devolvidos por falta de saldo no Banco Central (CCF).",
    long:
      "Registros no CCF (Cadastro de Emitentes de Cheques sem Fundos) do BACEN indicam que o consultado emitiu cheques sem saldo disponível. " +
      "Esse tipo de ocorrência demonstra risco operacional e pode dificultar a abertura de contas ou concessão de crédito.",
  },
  protestosBureau: {
    short: "Títulos protestados registrados em bureaus de crédito.",
    long:
      "Os protestos apontam dívidas não pagas formalizadas em cartório e comunicadas aos bureaus. " +
      "Mostram inadimplência confirmada e afetam diretamente a reputação e capacidade de crédito até que sejam quitadas.",
  },
  negativacoes: {
    short: "Registros ativos de inadimplência em bureaus de crédito.",
    long:
      "Negativações são apontamentos de dívidas vencidas e não pagas, registradas por empresas credoras. " +
      "Enquanto ativas, reduzem o score e dificultam o acesso a financiamentos, cartões e outros serviços de crédito.",
  },
  acoesCiveis: {
    short: "Processos judiciais envolvendo cobranças, execuções ou disputas.",
    long:
      "Ações cíveis mostram envolvimento do consultado em processos judiciais, que podem indicar risco jurídico ou financeiro. " +
      "É importante analisar o valor, o motivo e a frequência desses processos antes de conceder crédito.",
  },
  protestos: {
    short: "Títulos não pagos formalmente registrados em cartório.",
    long:
      "Os protestos comprovam que houve inadimplência e que o credor buscou o cartório para registro oficial. " +
      "Essas ocorrências impactam negativamente a credibilidade financeira até a baixa do título.",
  },
  chequesSustados: {
    short: "Cheques sustados por ordem do emitente.",
    long:
      "A sustação de cheque ocorre quando o emitente solicita o bloqueio da compensação — por perda, roubo ou disputa comercial. " +
      "Nem sempre indica inadimplência, mas deve ser analisada em conjunto com outras informações financeiras.",
  },
  chequesSemFundo: {
    short: "Cheques devolvidos por insuficiência de saldo.",
    long:
      "O histórico de cheques sem fundo mostra que o consultado emitiu cheques sem saldo suficiente para compensação. " +
      "É um sinal de desorganização financeira e pode gerar restrições futuras em crédito e relacionamento bancário.",
  },
  protestoNacional: {
    short: "Consulta nacional de protestos em cartórios conveniados.",
    long:
      "O levantamento nacional de protestos reúne registros de inadimplência em cartórios de todo o país. " +
      "Revela a quantidade, valores e datas de protestos, ajudando a avaliar o histórico de pagamentos do consultado.",
  },
  cadin: {
    short: "Dívidas com órgãos públicos federais e entidades governamentais.",
    long:
      "O CADIN (Cadastro Informativo de Créditos não Quitados do Setor Público Federal) reúne débitos com órgãos e empresas públicas. " +
      "Enquanto constarem registros, o consultado pode enfrentar restrições para contratar com o governo ou obter crédito público.",
  },
  cndt: {
    short: "Situação da Certidão Negativa de Débitos Trabalhistas (CNDT).",
    long:
      "A CNDT comprova se existem débitos trabalhistas inadimplidos em nome do consultado perante a Justiça do Trabalho. " +
      "Pendências na certidão podem impedir participação em licitações e sinalizam risco jurídico nas relações comerciais.",
  },
  antecedentesCriminais: {
    short: "Situação da certidão de antecedentes criminais.",
    long:
      "A certidão de antecedentes criminais informa se há registros vinculados ao documento consultado. " +
      "Quando existem apontamentos, o resultado sinaliza risco jurídico e exige avaliação complementar.",
  },
  mandadosPrisao: {
    short: "Consulta de mandados de prisão em aberto.",
    long:
      "A pesquisa de mandados de prisão identifica ordens judiciais pendentes de cumprimento em nome do consultado. " +
      "A existência de mandados representa alto risco jurídico e deve ser tratada com prioridade na análise.",
  },
  creditoVencidoBacen: {
    short: "Operações financeiras em atraso reportadas ao Banco Central.",
    long:
      "O campo ‘Crédito Vencido’ no SCR (Sistema de Informações de Crédito) mostra valores de operações bancárias em atraso. " +
      "É um indicador importante de risco, especialmente quando há aumento recente ou valores significativos.",
  },
  prejuizoBacen: {
    short: "Créditos baixados como perda definitiva no Banco Central.",
    long:
      "‘Prejuízo’ indica operações financeiras consideradas irrecuperáveis e baixadas como perda pelas instituições. " +
      "É um dos sinais mais severos de risco, refletindo dificuldade extrema em honrar compromissos financeiros.",
  },
  falenciaRecuperacao: {
    short: "Ocorrências de falência ou recuperação judicial.",
    long:
      "Registros de falência ou recuperação judicial demonstram grave desequilíbrio financeiro. " +
      "Essas situações comprometem a continuidade das atividades e exigem critérios rigorosos de análise de crédito.",
  },
};

const SEVERITY = {
  ALTA: 3,
  MEDIA: 2,
  BAIXA: 1,
};

function parseBRLNumber(v) {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const s = String(v).trim();
  if (!s) return 0;
  // aceita “1.234,56”, “1234,56”, “1234.56”
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function CustomAlertWrapper({ data }) {
  /** ===================== normalização do data.additional ===================== */
  const dataAlerts = React.useMemo(() => {
    if (!data) return null;

    const merged = { ...data };
    let add = data?.additional;

    if (typeof add === "string") {
      try {
        const t = add.trim();
        add = t && t !== "null" && t !== "undefined" ? JSON.parse(t) : null;
      } catch {
        add = null;
      }
    }

    if (!add) return merged;

    if (Array.isArray(add)) {
      add.forEach((item, idx) => {
        if (!item) return;
        const key =
          item?.key ||
          item?.alias ||
          item?.nome ||
          item?.name ||
          `additional_${idx + 1}`;
        merged[key] = item;
      });
    } else if (typeof add === "object") {
      Object.entries(add).forEach(([k, v]) => {
        if (v != null) merged[k] = v;
      });
    }

    return merged;
  }, [data]);

  /** ===================== caixas (com/sem ocorrência) + helper ===================== */
  const withOcc = React.useRef([]); // [{jsx, weight}]
  const withoutOcc = React.useRef([]); // [{jsx, weight}]

  const pushCard = ({
    has = false,
    count = 0,
    total = 0,
    severity = SEVERITY.BAIXA,
    jsx,
  }) => {
    // peso: prioriza “com ocorrência” + severidade + volume/valor
    const weight =
      (has ? 1000000 : 0) +
      severity * 10000 +
      Math.min(9999, Number(count) || 0) * 10 +
      Math.min(999999999, Number(total) || 0);
    (has ? withOcc.current : withoutOcc.current).push({ jsx, weight });
  };

  /** helper pra criar card rapidamente */
  const addCard = ({
    title,
    has,
    count,
    total,
    msg,
    descrShort,
    descrLong,
    severity = SEVERITY.BAIXA,
    href,
  }) => {
    pushCard({
      has,
      count: Number(count) || 0,
      total: Number(total) || 0,
      severity,
      href,
      jsx: (
        <AvisoCard
          key={`${title}-${msg}`}
          title={title}
          hasRestrictions={!!has}
          {...(total != null ? { total } : {})}
          messageError={msg}
          description={descrShort}
          details={descrLong}
          href={href}
        />
      ),
    });
  };

  /** ===================== construção dos cards (todas as fontes) ===================== */

  // ===== SCPC NET PF =====
  if (dataAlerts?.scpcNetPF?.statusRetorno == 1) {
    const res = dataAlerts?.scpcNetPF?.consultaCredito?.resumoConsulta || {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg > 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust > 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== ACERTA ESSENCIAL POSITIVO =====
  if (dataAlerts?.acertaEssencialPositivo?.statusRetorno == 1) {
    const res =
      dataAlerts?.acertaEssencialPositivo?.consultaCredito?.resumoConsulta ||
      {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg > 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const qA = +res?.acoesCiveis?.quantidadeTotal || 0;
    const vA = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "Ações Cíveis",
      has: qA > 0,
      count: qA,
      total: vA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-1",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    const vP = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust > 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });
  }

  // ===== ACERTA ESSENCIAL =====
  if (dataAlerts?.acertaEssencial?.statusRetorno == 1) {
    const res =
      dataAlerts?.acertaEssencial?.consultaCredito?.resumoConsulta || {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg > 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    const vP = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust > 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });
  }

  // ===== ACERTA COMPLETO =====
  if (dataAlerts?.acertaCompleto?.statusRetorno == 1) {
    const res =
      dataAlerts?.acertaCompleto?.consultaCredito?.resumoConsulta || {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg > 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const qA = +res?.acoesCiveis?.quantidadeTotal || 0;
    const vA = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "Ações Cíveis",
      has: qA > 0,
      count: qA,
      total: vA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-1",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    const vP = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust > 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });
  }

  // ===== DEFINE RISCO =====
  if (dataAlerts?.defineRisco?.statusRetorno == 1) {
    const res = dataAlerts?.defineRisco?.consultaCredito?.resumoConsulta || {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg !== 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf !== 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust !== 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    const vP = +res?.protestos?.valorTotal || 0;

    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });
  }

  // ===== DEFINE NEGÓCIO POSITIVO =====
  if (dataAlerts?.defineNegocioPositivo?.statusRetorno == 1) {
    const res =
      dataAlerts?.defineNegocioPositivo?.consultaCredito?.resumoConsulta || {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Negativações Registradas",
      has: qNeg !== 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf !== 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });

    const qSust = +res?.chequesSustados?.quantidadeTotal || 0;
    addCard({
      title: "Cheques Sustados",
      has: qSust !== 0,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      href: "ccs-base-1",
      severity: SEVERITY.BAIXA,
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    const vP = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      total: vP,
      count: qP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const qA = +res?.acoesCiveis?.quantidadeTotal || 0;
    const vA = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "Ações Cíveis",
      has: qA > 0,
      count: qA,
      total: vA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-1",
    });

    addCard({
      title: "Falências/Rec. Judiciais",
      has: +res?.falenciasRecJudiciais?.quantidadeTotal > 0,
      count: res?.falenciasRecJudiciais?.quantidadeTotal,
      msg: `${res?.falenciasRecJudiciais?.quantidadeTotal} ocorrências`,
      descrShort: DESCR.falenciaRecuperacao.short,
      descrLong: DESCR.falenciaRecuperacao.long,
      severity: SEVERITY.ALTA,
      href: "falencias-base-1",
    });
  }

  // ===== DEFINE LIMITE =====
  if (dataAlerts?.defineLimite?.statusRetorno == 1) {
    const dl = dataAlerts?.defineLimite || {};

    const hasCSF = !dl?.cheques_sem_fundo?.mensagem;
    const qCSF = +dl?.cheques_sem_fundo?.total_cheques_sem_fundos || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: hasCSF,
      count: qCSF,
      msg: `${qCSF} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });

    const hasSust = !dl?.cheques_sustados?.mensagem;
    const qSust = +dl?.cheques_sustados?.total || 0;
    addCard({
      title: "Cheques Sustados",
      has: hasSust,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const hasProt = !dl?.protestos?.mensagem;
    const qProt = +dl?.protestos?.total_protestos || 0;
    addCard({
      title: "Protestos",
      has: hasProt,
      count: qProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const hasPend = !dl?.pendencias_restricoes?.mensagem;
    const qPend = +dl?.pendencias_restricoes?.total_pendencias || 0;
    addCard({
      title: "Negativações Registradas",
      has: hasPend,
      count: qPend,
      msg: `${qPend} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const hasA = !dl?.acoes_civeis?.mensagem;
    const qA = +dl?.acoes_civeis?.total_acoes || 0;
    addCard({
      title: "Ações Cíveis",
      has: hasA,
      count: qA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-1",
    });

    const hasFal = !dl?.falencias_recuperacao_judicial?.mensagem;
    const qFal = +dl?.falencias_recuperacao_judicial?.quantidade_total || 0;
    addCard({
      title: "Falência/Recuperação",
      has: hasFal,
      count: qFal,
      msg: `${qFal} ocorrências`,
      descrShort: DESCR.falenciaRecuperacao.short,
      descrLong: DESCR.falenciaRecuperacao.long,
      severity: SEVERITY.ALTA,
      href: "falencias-base-1",
    });
  }

  // ===== DEFINE LIMITE POSITIVO =====
  if (dataAlerts?.defineLimitePositivo?.statusRetorno == 1) {
    const dlp = dataAlerts?.defineLimitePositivo || {};

    const hasCSF = +dlp?.cheques_sem_fundo?.total_cheques_sem_fundos > 0;
    const qCSF = +dlp?.cheques_sem_fundo?.total_cheques_sem_fundos || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: hasCSF,
      count: qCSF,
      msg: `${qCSF} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-1",
    });

    const hasSust = +dlp?.cheques_sustados?.total > 0;
    const qSust = +dlp?.cheques_sustados?.total || 0;
    addCard({
      title: "Cheques Sustados",
      has: hasSust,
      count: qSust,
      msg: `${qSust} ocorrências`,
      descrShort: DESCR.chequesSustados.short,
      descrLong: DESCR.chequesSustados.long,
      severity: SEVERITY.BAIXA,
      href: "ccs-base-1",
    });

    const hasProt = +dlp?.protestos?.total_protestos > 0;
    const vProt = +dlp?.protestos?.valor_total || 0;
    const qProt = +dlp?.protestos?.total_protestos || 0;
    addCard({
      title: "Protestos",
      has: hasProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-1",
    });

    const hasPend = +dlp?.pendencias_restricoes?.total_pendencias > 0;
    const qPend = +dlp?.pendencias_restricoes?.total_pendencias || 0;
    const vPend = +dlp?.pendencias_restricoes?.valor || 0;
    addCard({
      title: "Negativações Registradas",
      has: hasPend,
      total: vPend,
      msg: `${qPend} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-1",
    });

    const hasA = +dlp?.acoes_civeis?.total_acoes > 0;
    const qA = +dlp?.acoes_civeis?.total_acoes || 0;
    addCard({
      title: "Ações Cíveis",
      has: hasA,
      count: qA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-1",
    });

    const hasFal =
      +dlp?.falencias_recuperacao_judicial?.ultimas_ocorrencias
        ?.falencia_recuperacao_judicial?.length > 0;
    const qFal =
      dlp?.falencias_recuperacao_judicial?.ultimas_ocorrencias
        ?.falencia_recuperacao_judicial?.length || 0;
    addCard({
      title: "Falência/Recuperação",
      has: hasFal,
      count: qFal,
      msg: `${qFal} ocorrências`,
      descrShort: DESCR.falenciaRecuperacao.short,
      descrLong: DESCR.falenciaRecuperacao.long,
      severity: SEVERITY.ALTA,
      href: "falencias-base-1",
    });
  }

  // ===== CREDNET =====
  if (dataAlerts?.crednet?.statusRetorno == 1) {
    const res = dataAlerts?.crednet?.consultaCredito?.resumoConsulta || {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    if (res?.protestos) {
      const qProt = +res?.protestos?.quantidadeTotal || 0;
      const vProt = +res?.protestos?.valorTotal || 0;
      addCard({
        title: "II - Protestos",
        has: qProt > 0,
        count: qProt,
        total: vProt,
        msg: `${qProt} ocorrências`,
        descrShort: DESCR.protestos.short,
        descrLong: DESCR.protestos.long,
        severity: SEVERITY.MEDIA,
        href: "protestos-base-2",
      });
    }

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "II - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-2",
    });

    if (dataAlerts?.crednet?.consultaCredito?.limpaNome) {
      addCard({
        title: "ATENÇÃO",
        has: true,
        count: 1,
        msg: `Cautela na liberação de crédito`,
        descrShort: "CONSTA ALGUM PROCESSO DE INIBIÇÃO DE PENDÊNCIAS",
        descrLong: "CONSTA ALGUM PROCESSO DE INIBIÇÃO DE PENDÊNCIAS",
        severity: SEVERITY.ALTA,
      });
    }
  }

  // ===== SERASA RELATÓRIO BÁSICO PF =====
  if (dataAlerts?.serasaRelatorioBasicoPF?.statusRetorno == 1) {
    const res =
      dataAlerts?.serasaRelatorioBasicoPF?.consultaCredito?.resumoConsulta ||
      {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "II - Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-2",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "II - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-2",
    });
  }

  // ===== SERASA RELATÓRIO BÁSICO PJ =====
  if (dataAlerts?.serasaRelatorioBasicoPJ?.statusRetorno == 1) {
    const res =
      dataAlerts?.serasaRelatorioBasicoPJ?.consultaCredito?.resumoConsulta ||
      {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "II - Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-2",
    });

    const qCsf =
      +dataAlerts?.serasaRelatorioBasicoPF?.consultaCredito?.resumoConsulta
        ?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "II - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-2",
    });
  }

  // ===== SERASA RELATÓRIO AVANÇADO PJ =====
  if (dataAlerts?.serasaRelatorioAvancadoPJ?.statusRetorno == 1) {
    const res =
      dataAlerts?.serasaRelatorioAvancadoPJ?.consultaCredito?.resumoConsulta ||
      {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "II - Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-2",
    });

    const qCsf =
      +dataAlerts?.serasaRelatorioAvancadoPJ?.consultaCredito?.resumoConsulta
        ?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "II - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-2",
    });

    const recJud =
      +dataAlerts?.serasaRelatorioAvancadoPJ?.consultaCredito
        ?.falenciasRecJudiciais?.length || 0;
    addCard({
      title: "II - Falências e Recuperações Judiciais",
      has: recJud > 0,
      count: recJud,
      msg: `${recJud} ocorrências`,
      descrShort: DESCR.falenciaRecuperacao.short,
      descrLong: DESCR.falenciaRecuperacao.long,
      severity: SEVERITY.ALTA,
      href: "falencias-rec-judiciais-base-2",
    });
  }

  // ===== DÍVIDAS (genérico) =====
  if (dataAlerts?.dividas?.statusRetorno == 1) {
    const res = dataAlerts?.dividas?.resumoConsulta || {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== SCPC NET (genérico) =====
  if (dataAlerts?.scpcNet?.statusRetorno == 1) {
    const res = dataAlerts?.scpcNet?.resumoConsulta || {};

    const qP = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vP = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Pendências Financeiras",
      has: qP > 0,
      count: qP,
      total: vP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== SPC/SERASA PJ =====
  if (dataAlerts?.spcSerasaPJ?.statusRetorno == 1) {
    const serasa = dataAlerts?.spcSerasaPJ?.resumoConsulta?.serasa || {};
    const q = +serasa?.quantidadeTotal || 0;
    const v = +serasa?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: q > 0,
      count: q,
      total: v,
      msg: `${q} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const spc = dataAlerts?.spcSerasaPJ?.resumoConsulta?.spc || {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "III - Restrições em aberto",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.restricoes.short,
      descrLong: DESCR.restricoes.long,
      severity: SEVERITY.MEDIA,
      href: "restricoes-base-3",
    });

    const protestos = dataAlerts?.spcSerasaPJ?.resumoConsulta?.protestos || {};
    const qProtestos = +protestos?.quantidadeTotal || 0;
    const vprotestos = +protestos?.valorTotal || 0;
    addCard({
      title: "III - Protestos",
      has: qProtestos > 0,
      count: qProtestos,
      total: vprotestos,
      msg: `${qProtestos} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-3",
    });
  }

  // ===== Pend SPC/Serasa PF =====
  if (dataAlerts?.pendSPCSerasaPF?.statusRetorno == 1) {
    const resumo = dataAlerts?.pendSPCSerasaPF?.resumoConsulta || {};

    const serasa = resumo?.serasa || {};
    const qSer = +serasa?.quantidadeTotal || 0;
    const vSer = +serasa?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: qSer > 0,
      count: qSer,
      total: vSer,
      msg: `${qSer} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const spc = resumo?.spc || {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "III - Restrições em aberto",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.restricoes.short,
      descrLong: DESCR.restricoes.long,
      severity: SEVERITY.MEDIA,
      href: "restricoes-base-3",
    });
  }

  // ===== SPC/Serasa PF (genérico) =====
  if (dataAlerts?.spcSerasaPF?.statusRetorno == 1) {
    const serasa = dataAlerts?.spcSerasaPF?.resumoConsulta?.serasa || {};
    const q = +serasa?.quantidadeTotal || 0;
    const v = +serasa?.valorTotal || 0;
    addCard({
      title: "II - Pendências financeiras",
      has: q > 0,
      count: q,
      total: v,
      msg: `${q} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-2",
    });

    const spc = dataAlerts?.spcSerasaPF?.resumoConsulta?.spc || {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "III - Restrições em aberto",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.restricoes.short,
      descrLong: DESCR.restricoes.long,
      severity: SEVERITY.MEDIA,
      href: "restricoes-base-3",
    });

    const protestos = dataAlerts?.spcSerasaPF?.resumoConsulta?.protestos || {};
    const qProtestos = +protestos?.quantidadeTotal || 0;
    const vprotestos = +protestos?.valorTotal || 0;
    addCard({
      title: "III - Protestos",
      has: qProtestos > 0,
      count: qProtestos,
      total: vprotestos,
      msg: `${qProtestos} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });
  }

  if (
    dataAlerts?.spcTerceirosPF?.statusRetorno == 1 ||
    dataAlerts?.spcTerceirosPJ?.statusRetorno == 1
  ) {
    const serasa =
      dataAlerts?.spcTerceirosPF?.resumoConsulta?.serasa ||
      dataAlerts?.spcTerceirosPJ?.resumoConsulta?.serasa ||
      {};
    const q = +serasa?.quantidadeTotal || 0;
    const v = +serasa?.valorTotal || 0;
    addCard({
      title: "II - Pendências Financeiras",
      has: q > 0,
      count: q,
      total: v,
      msg: `${q} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
    });

    const spc =
      dataAlerts?.spcTerceirosPF?.resumoConsulta?.spc ||
      dataAlerts?.spcTerceirosPJ?.resumoConsulta?.spc ||
      {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "III - Restrições em aberto",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.restricoes.short,
      descrLong: DESCR.restricoes.long,
      severity: SEVERITY.MEDIA,
    });

    const protestos =
      dataAlerts?.spcTerceirosPF?.resumoConsulta?.protestos ||
      dataAlerts?.spcTerceirosPJ?.resumoConsulta?.protestos ||
      {};
    const qProtestos = +protestos?.quantidadeTotal || 0;
    const vprotestos = +protestos?.valorTotal || 0;
    addCard({
      title: "III - Protestos",
      has: qProtestos > 0,
      count: qProtestos,
      total: vprotestos,
      msg: `${qProtestos} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });
  }

  if (dataAlerts?.spcMais?.statusRetorno == 1) {
    const pendenciasFinanceiras =
      dataAlerts?.spcMais?.resumoConsulta?.pendenciasFinanceiras || {};
    const q = +pendenciasFinanceiras?.quantidadeTotal || 0;
    const v = +pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "III - Restrições Financeiras",
      has: q > 0,
      count: q,
      total: v,
      msg: `${q} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
    });

    const protestos = dataAlerts?.spcMais?.resumoConsulta?.protestos || {};
    const qProtestos = +protestos?.quantidadeTotal || 0;
    const vprotestos = +protestos?.valorTotal || 0;
    addCard({
      title: "III - Protestos",
      has: qProtestos > 0,
      count: qProtestos,
      total: vprotestos,
      msg: `${qProtestos} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
    });

    const csf = dataAlerts?.spcMais?.resumoConsulta?.chequesSemFundo || {};
    const qCsf =
      +dataAlerts?.spcMais?.resumoConsulta?.chequesSemFundo?.quantidadeTotal ||
      0;
    addCard({
      title: "III - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== SPC MIX TOP PF =====
  if (dataAlerts?.spcMixTopPF?.statusRetorno == 1) {
    const res = dataAlerts?.spcMixTopPF?.resumoConsulta || {};

    const spc = res?.spc || {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "Pendências/Restrições III",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
    });

    const csf = res?.chequesSemFundo || {};
    const qCsf = +csf?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos III",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== SPC MIX TOP PJ =====
  if (dataAlerts?.spcMixTopPJ?.statusRetorno == 1) {
    const res = dataAlerts?.spcMixTopPJ?.resumoConsulta || {};

    const spc = res?.spc || {};
    const qSpc = +spc?.quantidadeTotal || 0;
    const vSpc = +spc?.valorTotal || 0;
    addCard({
      title: "Pendências/Restrições III",
      has: qSpc > 0,
      count: qSpc,
      total: vSpc,
      msg: `${qSpc} ocorrências`,
      descrShort: DESCR.pendencias.short,
      descrLong: DESCR.pendencias.long,
      severity: SEVERITY.MEDIA,
    });

    const csfPF =
      dataAlerts?.spcMixTopPF?.resumoConsulta?.chequesSemFundo || {};
    const qCsf = +csfPF?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos III",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== QUOD =====
  if (dataAlerts?.quod?.statusRetorno == 1) {
    const res = dataAlerts?.quod?.resumoConsulta || {};

    const qPb = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vPb = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "IV - Pendências Bancárias",
      has: qPb > 0,
      count: qPb,
      total: vPb,
      msg: `${qPb} ocorrências`,
      descrShort: DESCR.pendenciasBancarias.short,
      descrLong: DESCR.pendenciasBancarias.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-4",
    });

    const qA = +res?.acoesCiveis?.quantidadeTotal || 0;
    const vA = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "IV - Ações Cíveis",
      has: qA > 0,
      count: qA,
      total: vA,
      msg: `${qA} ocorrências`,
      descrShort: DESCR.acoesCiveisBureau.short,
      descrLong: DESCR.acoesCiveisBureau.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-4",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "IV - Cheques sem Fundos",
      has: qCsf > 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundoBACEN.short,
      descrLong: DESCR.chequesSemFundoBACEN.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-4",
    });

    const qProt = +res?.protestos?.quantidadeTotal || 0;
    const vProt = +res?.protestos?.valorTotal || 0;
    addCard({
      title: "IV - Protestos",
      has: qProt > 0,
      count: qProt,
      total: vProt,
      msg: `${qProt} ocorrências`,
      descrShort: DESCR.protestosBureau.short,
      descrLong: DESCR.protestosBureau.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-4",
    });
  }

  if (dataAlerts?.cadastralRestritivoScorePJ?.statusRetorno == 1) {
    const res =
      dataAlerts?.cadastralRestritivoScorePJ?.consultaCredito?.resumoConsulta ||
      {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Restrições Financeiras",
      has: qNeg !== 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-4",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf !== 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-4",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-4",
    });

    const aP = +res?.acoesCiveis?.quantidadeTotal || 0;
    const aPV = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "Ações Cíveis",
      has: aP > 0,
      count: aP,
      total: aPV,
      msg: `${aP} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-4",
    });
  }

  if (dataAlerts?.cadastralRestritivoScorePF?.statusRetorno == 1) {
    const res =
      dataAlerts?.cadastralRestritivoScorePF?.consultaCredito?.resumoConsulta ||
      {};

    const qNeg = +res?.pendenciasFinanceiras?.quantidadeTotal || 0;
    const vNeg = +res?.pendenciasFinanceiras?.valorTotal || 0;
    addCard({
      title: "Restrições Financeiras",
      has: qNeg !== 0,
      count: qNeg,
      total: vNeg,
      msg: `${qNeg} ocorrências`,
      descrShort: DESCR.negativacoes.short,
      descrLong: DESCR.negativacoes.long,
      severity: SEVERITY.MEDIA,
      href: "negativacoes-base-4",
    });

    const qCsf = +res?.chequesSemFundo?.quantidadeTotal || 0;
    addCard({
      title: "Cheques sem Fundos",
      has: qCsf !== 0,
      count: qCsf,
      msg: `${qCsf} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
      href: "ccf-base-4",
    });

    const qP = +res?.protestos?.quantidadeTotal || 0;
    addCard({
      title: "Protestos",
      has: qP > 0,
      count: qP,
      msg: `${qP} ocorrências`,
      descrShort: DESCR.protestos.short,
      descrLong: DESCR.protestos.long,
      severity: SEVERITY.MEDIA,
      href: "protestos-base-4",
    });

    const aP = +res?.acoesCiveis?.quantidadeTotal || 0;
    const aPV = +res?.acoesCiveis?.valorTotal || 0;
    addCard({
      title: "Ações Cíveis",
      has: aP > 0,
      count: aP,
      total: aPV,
      msg: `${aP} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-base-4",
    });
  }

  // ===== PROTESTO NACIONAL =====
  if (dataAlerts?.protestoCENPROT?.statusRetorno == 1) {
    const qtd = dataAlerts?.protestoCENPROT?.numeroTotalProtestos || 0;
    const vTot = dataAlerts?.protestoCENPROT?.valorTotalProtestos || 0;
    addCard({
      title: "Protesto Nacional",
      has: qtd > 0,
      count: qtd,
      total: vTot,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.protestoNacional.short,
      descrLong: DESCR.protestoNacional.long,
      severity: SEVERITY.MEDIA,
      href: "protesto-nacional",
    });
  }

  if (dataAlerts?.protestoNacional?.statusRetorno == 1) {
    const qtd = dataAlerts?.protestoNacional?.ocorrencias?.length || 0;
    const vTot =
      +dataAlerts?.protestoNacional?.resumoConsulta?.protestos?.valorTotal || 0;
    addCard({
      title: "Protesto Nacional",
      has: qtd > 0,
      count: qtd,
      total: vTot,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.protestoNacional.short,
      descrLong: DESCR.protestoNacional.long,
      severity: SEVERITY.MEDIA,
      href: "protesto-nacional",
    });
  }

  // ===== PROTESTO NACIONAL =====
  if (dataAlerts?.protestoNacional2?.statusRetorno == 1) {
    const qtd = dataAlerts?.protestoNacional2?.ocorrencias?.length || 0;
    const vTot =
      +dataAlerts?.protestoNacional2?.resumoConsulta?.protestos?.valorTotal ||
      0;
    addCard({
      title: "Protesto Nacional",
      has: qtd > 0,
      count: qtd,
      total: vTot,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.protestoNacional.short,
      descrLong: DESCR.protestoNacional.long,
      severity: SEVERITY.MEDIA,
      href: "protesto-nacional",
    });
  }

  // ===== CHEQUES SEM FUNDO (fonte dedicada) =====
  if (dataAlerts?.chequesSemFundo?.statusRetorno == 1) {
    const qtd = dataAlerts?.chequesSemFundo?.ocorrencias?.length || 0;
    addCard({
      title: "Cheques Sem Fundo",
      has: qtd > 0,
      count: qtd,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.chequesSemFundo.short,
      descrLong: DESCR.chequesSemFundo.long,
      severity: SEVERITY.MEDIA,
    });
  }

  // ===== CADIN =====
  if (dataAlerts?.cadin?.statusRetorno == 1) {
    addCard({
      title: "CADIN",
      has: +dataAlerts?.cadin?.qtd_dividas > 0,
      count: dataAlerts?.cadin?.qtd_dividas,
      msg: `${dataAlerts?.cadin?.qtd_dividas} ocorrências`,
      descrShort: DESCR.cadin.short,
      descrLong: DESCR.cadin.long,
      severity: SEVERITY.MEDIA,
      href: "cadin",
    });
  }

  // ===== SCR BACEN =====
  if (dataAlerts?.scrBacen?.statusRetorno == 1) {
    const cons = dataAlerts?.scrBacen?.scrBacen?.consolidado || {};

    const vVenc = parseBRLNumber(cons?.creditoVencido?.valor);
    const hasVenc = vVenc > 0;
    pushCard({
      has: hasVenc,
      count: 1,
      total: vVenc,
      severity: SEVERITY.ALTA,
      jsx: (
        <AvisoCard
          title="Crédito Vencido - BACEN"
          hasRestrictions={hasVenc}
          messageError={`${formatToReal(cons?.creditoVencido?.valor)}`}
          description={DESCR.creditoVencidoBacen.short}
          details={DESCR.creditoVencidoBacen.long}
          href="credito-vencido-bacen"
        />
      ),
    });

    const vPrej = parseBRLNumber(cons?.prejuizo?.valor);
    const hasPrej = vPrej > 0;
    pushCard({
      has: hasPrej,
      count: 1,
      total: vPrej,
      severity: SEVERITY.ALTA,
      jsx: (
        <AvisoCard
          title="Prejuízo - BACEN"
          hasRestrictions={hasPrej}
          messageError={`${formatToReal(cons?.prejuizo?.valor)}`}
          description={DESCR.prejuizoBacen.short}
          details={DESCR.prejuizoBacen.long}
          href="prejuizo-bacen"
        />
      ),
    });
  }

  // ===== AÇÕES/PROCESSOS =====
  if (dataAlerts?.acoesProcessos?.statusRetorno == 1) {
    const qtd = dataAlerts?.acoesProcessos?.acoes?.processos?.length || 0;
    addCard({
      title: "Ações e Processos Judiciais",
      has: qtd > 0,
      count: qtd,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.acoesCiveis.short,
      descrLong: DESCR.acoesCiveis.long,
      severity: SEVERITY.MEDIA,
      href: "acoes-processos-judiciais",
    });
  }

  // ===== CNDT =====
  if (dataAlerts?.cndt?.statusRetorno == 1) {
    const qtd = +dataAlerts?.cndt?.totalProcessos || 0;
    addCard({
      title: "CNDT",
      has: qtd > 0,
      count: qtd,
      msg: `${qtd} ocorrências`,
      descrShort: DESCR.cndt.short,
      descrLong: DESCR.cndt.long,
      severity: SEVERITY.MEDIA,
      href: "cndt",
    });
  }

  // ===== ANTECEDENTES CRIMINAIS =====
  if (dataAlerts?.antecedentesCriminais) {
    const hasAnte =
      !!dataAlerts?.antecedentesCriminais?.possuiAntecedentesCriminais;
    addCard({
      title: "Antecedentes Criminais",
      has: hasAnte,
      count: hasAnte ? 1 : 0,
      msg: hasAnte ? "Consta apontamento" : "Sem apontamento",
      descrShort: DESCR.antecedentesCriminais.short,
      descrLong: DESCR.antecedentesCriminais.long,
      severity: SEVERITY.ALTA,
      href: "antecedentes-criminais",
    });
  }

  // ===== MANDADOS DE PRISAO =====
  if (dataAlerts?.mandadosPrisao) {
    const qtd = +dataAlerts?.mandadosPrisao?.mandadosPrisao?.length || 0;
    const hasMandado = !!dataAlerts?.mandadosPrisao?.possuiMandado || qtd > 0;

    addCard({
      title: "Mandados de Prisão",
      has: hasMandado,
      count: qtd,
      msg: hasMandado ? `${qtd} mandado(s)` : "Sem mandado",
      descrShort: DESCR.mandadosPrisao.short,
      descrLong: DESCR.mandadosPrisao.long,
      severity: SEVERITY.ALTA,
      href: "mandados-prisao",
    });
  }

  /** ===================== ordenação + UI (grids, resumo, ver mais) ===================== */
  const withSorted = React.useMemo(
    () => [...withOcc.current].sort((a, b) => b.weight - a.weight),
    [dataAlerts],
  );
  const withoutSorted = React.useMemo(
    () => [...withoutOcc.current].sort((a, b) => b.weight - a.weight),
    [dataAlerts],
  );

  const [showAllWith, setShowAllWith] = React.useState(false);
  const [showAllWithout, setShowAllWithout] = React.useState(false);
  const LIMIT = 6;

  const renderList = (items, showAll, setShowAll) => {
    const list = showAll ? items : items.slice(0, LIMIT);
    return (
      <>
        <div className="grid grid-cols-3 gap-3">
          {list.map((it, idx) => (
            <React.Fragment key={idx}>{it.jsx}</React.Fragment>
          ))}
        </div>
        {items.length > LIMIT && (
          <div className="mt-3 flex justify-center">
            <button
              className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "Mostrar menos" : `Ver mais (${items.length - LIMIT})`}
            </button>
          </div>
        )}
      </>
    );
  };

  const totalWith = withSorted.length;
  const totalWithout = withoutSorted.length;

  return (
    <>
      <TitleStripe>
        <div className="flex items-center gap-2">
          <FiAlertTriangle className="text-red-500" />
          <span>Alerta de Restrições</span>
        </div>
      </TitleStripe>

      {/* Seção: Com ocorrências */}
      {totalWith > 0 && (
        <section className="mb-2">
          <h4 className="mb-2 font-semibold text-red-700">
            Com ocorrências{" "}
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 border border-red-200 inline-block ml-1">
              {totalWith}
            </span>
          </h4>
          {renderList(withSorted, showAllWith, setShowAllWith)}
        </section>
      )}

      {/* Seção: Sem ocorrências (colapsável) */}
      {totalWithout > 0 && (
        <section className="mb-2">
          <details className="group" open>
            <summary className="cursor-pointer select-none list-none inline-flex items-center gap-2 text-gray-700 hover:text-gray-900">
              <span className="font-semibold">Sem ocorrências</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                {totalWithout}
              </span>
            </summary>
            <div className="mt-3">
              {renderList(withoutSorted, showAllWithout, setShowAllWithout)}
            </div>
          </details>
        </section>
      )}
    </>
  );
}

export default CustomAlertWrapper;
